import { Alert } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { selectStatusRefreshAlert, StatusRefreshOutcome } from '../domain/statusRefresh';
import useQuery from '../hooks/useQuery';

/** Re-read every five minutes, so the warning appears without reloading the page. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

interface StatusRefreshStatus {
    lastRun: StatusRefreshOutcome | null;
}

interface StatusRefreshAlertProps {
    /** Changes after a manual run, so its outcome shows at once instead of at the next poll. */
    refreshToken: number;
}

/**
 * Warns in the Control Room when the contract status refresh failed or has not
 * run for more than a day. It failed silently for almost five months, with
 * only the server log knowing (bridge day check 2026-10-26, G-1).
 */
const StatusRefreshAlert = ({ refreshToken }: StatusRefreshAlertProps) => {
    const { t } = useTranslation();
    const { data, reload } = useQuery<StatusRefreshStatus>({
        path: '/statuts/status',
        pollInterval: POLL_INTERVAL_MS,
    });

    useEffect(() => {
        if (refreshToken > 0) {
            reload();
        }
    }, [refreshToken, reload]);

    const alert = selectStatusRefreshAlert(data?.lastRun, new Date());
    if (!alert) {
        return null;
    }

    const finishedAt = dayjs(alert.finishedAt).format('YYYY-MM-DD HH:mm');
    const failed = alert.kind === 'failed';

    return (
        <Alert
            type={failed ? 'error' : 'warning'}
            showIcon
            style={{ marginBottom: 16 }}
            data-testid="status-refresh-alert"
            message={
                failed
                    ? t('controlRoom.statusRefresh.failedTitle', { finishedAt })
                    : t('controlRoom.statusRefresh.staleTitle', { finishedAt })
            }
            description={
                failed
                    ? t('controlRoom.statusRefresh.failedDescription', { error: alert.error })
                    : t('controlRoom.statusRefresh.staleDescription')
            }
        />
    );
};

export default StatusRefreshAlert;
